import type { UseComboboxStateChangeTypes } from 'downshift';
import { useCombobox } from 'downshift';

import type { IOptions } from '../../../shared/utils/options';
import type { TInputDropdownEventType } from '../../../shared/utils/types';

import type { IInputDropdownProps, TSearchRules } from '../_props';

/**
 * Typings
 */

type TUseSelectItem = {
  filterable?: boolean;
  options: IOptions[];
  isSelect?: boolean;
  searchRules: Required<TSearchRules>;
};

type THandleSelectItem = {
  selectedItem?: IOptions | null;
  setListOptions: (listOptions: IOptions[]) => void;
};

type TPropagateSelectItem = {
  selectedItem?: IOptions | null;
  type: UseComboboxStateChangeTypes;
} & Pick<IInputDropdownProps, 'onChangeSelect'>;

/**
 * Dropdown Actions Map
 */

const dropdownAction = new Map<UseComboboxStateChangeTypes, TInputDropdownEventType>([
  [useCombobox.stateChangeTypes.InputKeyDownEnter, 'Select'],
  [useCombobox.stateChangeTypes.ItemClick, 'Select'],
  [useCombobox.stateChangeTypes.InputBlur, 'Tab'],
]);

/**
 * Hook
 */

export const useSelectItem = ({ filterable, options, isSelect, searchRules }: TUseSelectItem) => {
  /**
   * Handlers
   */

  const handleSelectItem = ({ selectedItem, setListOptions }: THandleSelectItem) => {
    if (!filterable || isSelect) return;

    const filteredOptions = options
      .filter((option) => option.label.toLowerCase().includes(selectedItem?.label.toLowerCase() || ''))
      .filter((option) => searchRules.filterRule(option, selectedItem?.label))
      .sort(searchRules.sortRule(selectedItem?.label));

    setListOptions(filteredOptions);
  };

  const propagateSelectItem = ({ selectedItem, onChangeSelect, type }: TPropagateSelectItem) => {
    if (!selectedItem) return;

    onChangeSelect?.(selectedItem, dropdownAction.get(type));
  };

  /**
   * Return
   */

  return { handleSelectItem, propagateSelectItem };
};

export default useSelectItem;
